import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Info } from "lucide-react";

// Average-outstanding-balance approximation for MCA APR:
//   APR ≈ (F - 1) × 365 / avg_days × 100
//   avg_days ≈ months × 30 / 2  (straight-line paydown)
// Matches the 60–130% range cited in the "How MCA Factor Rates Work" article.
const estimateAPR = (factor, months) => {
  if (factor <= 1 || months <= 0) return 0;
  const avgDays = (months * 30) / 2;
  return ((factor - 1) * 365 / avgDays) * 100;
};

const money = (n) => `$${Math.round(n).toLocaleString()}`;

export const FactorRateConverter = () => {
  const [advance, setAdvance] = useState(100000);
  const [factor, setFactor] = useState(1.35);
  const [months, setMonths] = useState(8);

  const { payback, fee, apr, dailyDebit } = useMemo(() => {
    const p = advance * factor;
    const f = p - advance;
    const a = estimateAPR(factor, months);
    // ~21 business days/month for daily-debit MCAs
    const d = p / (months * 21);
    return { payback: p, fee: f, apr: a, dailyDebit: d };
  }, [advance, factor, months]);

  return (
    <section
      id="factor-converter"
      className="py-32 md:py-44 border-t border-black/10"
      style={{ background: "linear-gradient(180deg, transparent, rgba(196,164,124,0.05) 80%)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8 items-end mb-12">
          <div className="col-span-12 md:col-span-7">
            <div className="overline">MCA translator · Factor → APR</div>
            <h2 className="mt-4 font-serif text-5xl md:text-6xl leading-[0.95]">
              What does a <em className="italic text-[color:var(--gold)]">1.35 factor</em> actually cost?
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5">
            <p className="text-[color:var(--bone)]/75 leading-relaxed">
              Merchant cash advances are quoted in factor rates, not APR — and the two numbers look wildly different. Enter a deal below and we'll translate it into the honest cost of capital.
            </p>
          </div>
        </div>

        <div className="border border-black/10 bg-[color:var(--bg-elev)] p-8 md:p-12">
          <div className="grid grid-cols-12 gap-8">
            {/* Inputs */}
            <div className="col-span-12 md:col-span-5 space-y-10">
              {/* Advance */}
              <div>
                <div className="flex justify-between items-baseline mb-3">
                  <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/60">
                    Advance amount
                  </label>
                  <div data-testid="mca-advance-display" className="font-serif text-3xl text-[color:var(--gold)]">
                    ${(advance / 1000).toFixed(0)}K
                  </div>
                </div>
                <input
                  data-testid="mca-advance-slider"
                  type="range"
                  min={5000}
                  max={500000}
                  step={5000}
                  value={advance}
                  onChange={(e) => setAdvance(Number(e.target.value))}
                  className="w-full estimator-slider"
                  style={{ "--val": `${((advance - 5000) / (500000 - 5000)) * 100}%` }}
                  aria-label="Advance amount"
                />
                <div className="mt-2 flex justify-between font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--bone)]/40">
                  <span>$5K</span>
                  <span>$500K</span>
                </div>
              </div>

              {/* Factor */}
              <div>
                <div className="flex justify-between items-baseline mb-3">
                  <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/60">
                    Factor rate
                  </label>
                  <div data-testid="mca-factor-display" className="font-serif text-3xl text-[color:var(--gold)]">
                    {factor.toFixed(2)}×
                  </div>
                </div>
                <input
                  data-testid="mca-factor-slider"
                  type="range"
                  min={1.15}
                  max={1.55}
                  step={0.01}
                  value={factor}
                  onChange={(e) => setFactor(Number(e.target.value))}
                  className="w-full estimator-slider"
                  style={{ "--val": `${((factor - 1.15) / (1.55 - 1.15)) * 100}%` }}
                  aria-label="Factor rate"
                />
                <div className="mt-2 flex justify-between font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--bone)]/40">
                  <span>1.15</span>
                  <span>1.55</span>
                </div>
              </div>

              {/* Term */}
              <div>
                <div className="flex justify-between items-baseline mb-3">
                  <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/60">
                    Payback period
                  </label>
                  <div data-testid="mca-term-display" className="font-serif text-3xl text-[color:var(--gold)]">
                    {months} mo
                  </div>
                </div>
                <input
                  data-testid="mca-term-slider"
                  type="range"
                  min={3}
                  max={18}
                  step={1}
                  value={months}
                  onChange={(e) => setMonths(Number(e.target.value))}
                  className="w-full estimator-slider"
                  style={{ "--val": `${((months - 3) / (18 - 3)) * 100}%` }}
                  aria-label="Payback period"
                />
                <div className="mt-2 flex justify-between font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--bone)]/40">
                  <span>3 mo</span>
                  <span>18 mo</span>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="col-span-12 md:col-span-7 md:pl-12 md:border-l border-black/10">
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50 mb-8">
                The honest math
              </div>

              <div className="grid grid-cols-2 gap-6 mb-10">
                <div className="border-t border-black/10 pt-5">
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/55">
                    Total payback
                  </div>
                  <div data-testid="mca-payback" className="mt-2 font-serif text-4xl md:text-5xl text-[color:var(--bone)]">
                    {money(payback)}
                  </div>
                </div>
                <div className="border-t border-black/10 pt-5">
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/55">
                    Total fee
                  </div>
                  <div data-testid="mca-fee" className="mt-2 font-serif text-4xl md:text-5xl text-[color:var(--bone)]">
                    {money(fee)}
                  </div>
                </div>
                <div className="border-t border-black/10 pt-5">
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/55">
                    Est. daily debit
                  </div>
                  <div data-testid="mca-daily" className="mt-2 font-serif text-4xl md:text-5xl text-[color:var(--bone)]">
                    {money(dailyDebit)}
                  </div>
                </div>
                <div className="border-t-2 border-[color:var(--gold)] pt-5">
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--gold)]">
                    Effective APR
                  </div>
                  <motion.div
                    data-testid="mca-apr"
                    key={apr.toFixed(0)}
                    initial={{ opacity: 0.4 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.35 }}
                    className="mt-2 font-serif text-4xl md:text-5xl text-[color:var(--gold)]"
                  >
                    {apr.toFixed(0)}%
                  </motion.div>
                </div>
              </div>

              <div className="flex items-start gap-3 text-[color:var(--bone)]/70 text-sm leading-relaxed mb-8">
                <Info size={16} className="mt-1 flex-shrink-0 text-[color:var(--gold)]" />
                <p>
                  Effective APR is estimated using the average-outstanding-balance method (industry standard). Actual APR varies with daily-debit frequency, holdback percentage, and any prepayment discount. Always request the payback total and estimated term in writing before signing.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Link
                  to="/apply"
                  className="inline-flex items-center gap-3 bg-[color:var(--gold)] text-black px-6 py-3 font-mono text-xs tracking-[0.24em] uppercase hover:bg-[color:var(--gold-strong)] transition-colors"
                >
                  See lower-cost options <ArrowRight size={14} />
                </Link>
                <Link
                  to="/blog/how-mca-factor-rates-work"
                  className="gold-link font-mono text-xs tracking-[0.24em] uppercase text-[color:var(--gold)]"
                >
                  Read the full breakdown →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FactorRateConverter;
